
import { useEffect, useState } from 'react';

import { getRolePermissions } from '../../../auth/services/rolePermissions.service';

import type {
  PublicReportItem,
  PublicReportResponse,
} from '../types/publicReport.types';

interface RolePermission {
  permissionName: string;
  permissionActive: boolean;
  permissionDelete: boolean;
  roleActive: boolean;
  roleDelete: boolean;
  rolePermissionActive: boolean;
  rolePermissionDelete: boolean;
}

interface RolePermissionsResponse {
  permissions?: RolePermission[];
}

const REPORT_PERMISSION_MAP: Record<string, string> = {
  'activity-log': 'ActivityLogReport',
  'summary-report': 'SummaryReport',
  'access-creation': 'AccessCreationReport',
  'access-revocation': 'AccessRevocationReport',
  'access-modification': 'AccessModificationReport',
  'tracking-report': 'TrackingReport',
};

const ALL_REPORTS: PublicReportItem[] = [
  {
    id: 'activity-log',
    title: 'Activity Log',
    description:
      'View and export WorkVista activity log data for the selected date range.',
    path: '/public-report/activity-log',
    category: 'Activity',
  },
  {
    id: 'summary-report',
    title: 'Summary Report',
    description:
      'View and export a summary of WorkVista activity for the selected date range.',
    path: '/public-report/summary-report',
    category: 'Activity',
  },
  {
    id: 'access-creation',
    title: 'Access Creation',
    description:
      'View newly created access and role assignments within WorkVista.',
    path: '/public-report/access-creation',
    category: 'Access Governance',
  },
  {
    id: 'access-revocation',
    title: 'Access Revocation',
    description:
      'View access removed from separated or deactivated WorkVista users.',
    path: '/public-report/access-revocation',
    category: 'Access Governance',
  },
  {
    id: 'access-modification',
    title: 'Access Modification',
    description:
      'View access and role changes made for existing WorkVista users.',
    path: '/public-report/access-modification',
    category: 'Access Governance',
  },
  {
    id: 'tracking-report',
    title: 'Tracking Report',
    description:
      'View installation compliance, data capture status, host details and build information.',
    path: '/public-report/tracking-report',
    category: 'Compliance',
  },
];

const normalizePermissionName = (name: string): string => {
  const trimmedName = name.trim();
  const lastSeparatorIndex = trimmedName.lastIndexOf('.');

  return (
    lastSeparatorIndex >= 0
      ? trimmedName.substring(lastSeparatorIndex + 1)
      : trimmedName
  )
    .trim()
    .toLowerCase();
};

const isPermissionActive = (permission: RolePermission): boolean =>
  permission.permissionActive === true &&
  permission.permissionDelete === false &&
  permission.roleActive === true &&
  permission.roleDelete === false &&
  permission.rolePermissionActive === true &&
  permission.rolePermissionDelete === false;

const usePublicReport = () => {
  const [data, setData] = useState<PublicReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Refresh report access when the selected role changes.
  const selectedRoleId = localStorage.getItem('roleId') ?? '';

  useEffect(() => {
    let cancelled = false;

    const loadReports = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const employeeCode = localStorage.getItem('employeeCode');
        const roleIdValue = localStorage.getItem('roleId');
        const token = localStorage.getItem('token');

        if (!token || !employeeCode || !roleIdValue) {
          throw new Error(
            'Employee code or selected role is missing. Please sign in again.',
          );
        }

        const roleId = Number(roleIdValue);

        if (!Number.isInteger(roleId) || roleId <= 0) {
          throw new Error('The selected role ID is invalid.');
        }

        const result = (await getRolePermissions(
          employeeCode,
          roleId,
        )) as RolePermissionsResponse;

        const activePermissions = (
          Array.isArray(result?.permissions) ? result.permissions : []
        ).filter(isPermissionActive);

        const permissionNames = new Set(
          activePermissions.map((permission) =>
            normalizePermissionName(permission.permissionName ?? ''),
          ),
        );

        const permittedReports = ALL_REPORTS.filter((report) => {
          const requiredPermission = REPORT_PERMISSION_MAP[report.id];

          return (
            Boolean(requiredPermission) &&
            permissionNames.has(
              normalizePermissionName(requiredPermission),
            )
          );
        });

        if (cancelled) {
          return;
        }

        setData({
          projectName: 'WorkVista',
          projectDescription:
            'Access governance and compliance audit reports for the WorkVista application.',
          reports: permittedReports,
        });
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }

        console.error('Failed to load public report permissions:', err);

        setError(
          err instanceof Error
            ? err.message
            : 'Unable to load report permissions.',
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadReports();

    return () => {
      cancelled = true;
    };
  }, [selectedRoleId]);

  return { data, loading, error };
};

export default usePublicReport;
